/**
 * Daily Spend & Revenue — the fifth Reporting tab (task 055-03; epic 055).
 *
 * Chelsie's sheet as one screen: the month summary with both dedup grains
 * (raw sums of the day rows beside the month-deduped billing grain, README
 * decision 4), the daily grid in her column order with one column per co-op
 * campaign (`–` not running, blank 0, a `capped` superscript at a daily cap,
 * decision 8), the per-traffic-source buying-terms block, and the manual
 * Display Ad Spend editor (PUT/DELETE /admin/daily-spend-entries/<date>,
 * decision 11) in the sites-screen dialog idiom.
 *
 * Task 094-04 (D3/D4): a Source dropdown beside the range filters — All
 * sources · Facebook · Unattributed · each site — URL-persisted
 * (`?source=`), refetching the stats with the filter. Under a filter the
 * campaign matrix and the Traffic sources blocks hide (cross-source
 * artifacts), and the Display Ad Spend editor edits the day's row for that
 * source (grain-rule aware: a 409 offers the explicit convert choice).
 *
 * Task 094-06 (Q26): the External sources card below Traffic sources —
 * external-partner leads by site, month totals only. It follows `?source=`
 * (alive under `all` and its own site key) and hides on the empty array the
 * api returns under any non-matching filter; Traffic sources stays pure CTO.
 *
 * No column sorts: the day rows render in the query's calendar order (her
 * sheet's order), and `campaigns` / `sources` arrive ordered from the query
 * — no `SortableHeader` on this screen. Every pinned label and help string
 * is epic README §Decisions pre-resolved, byte for byte; no rendered string
 * names an ADR id, a Q-registry number or the net-rate constant.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FACEBOOK_SITE_ID } from '@leads-platform/shared';
import { api } from '../../lib/api';
import { fmtCount, fmtDate, fmtMoney } from '../../lib/format';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  deleteDailySpendEntry,
  fetchDailySpendStats,
  putDailySpendEntry,
  type DailySpendCampaignDay,
  type DailySpendDayRow,
  type DailySpendStatsResponse,
  type DailySpendTotals,
} from './daily-spend-api';
import { RangeFilters, TestLeadsToggle, useReportRange } from './report-filters';
import { cell, EmptyRow, ErrorBanner, LoadingRow } from './report-table';
import { ReportShell } from './report-shell';

// Header help, verbatim from epic 055 README §Decisions pre-resolved.
const LEADS_HELP = 'Distinct addresses captured this day across every offer type';
const EPL_HELP = '(Custom Page + Co-op) ÷ Leads';
const TOTAL_REVENUE_HELP =
  '90% of Custom Page + Co-op, matching the current sheet; the 10% adjustment is being confirmed';
const TOTAL_SPEND_HELP = 'Display Ad Spend + CTO Ad Spend';
const DISPLAY_AD_SPEND_HELP = "Entered by hand — Meta's number until the API link exists";
const CTO_AD_SPEND_HELP = "Unique leads per traffic source × the source's CPL";
const CPL_HELP = 'Total Spend ÷ Leads';
const COREG_PLUS_LEAD_HELP = "Half the co-op revenue per lead, as a percent — her sheet's column";
const TOTAL_UNIQUE_LEADS_HELP =
  'Leads delivered to co-op campaigns this day — the same lead counts once per campaign it went to, so this is not the distinct-address Leads column';
const MONTHLY_TOTAL_HELP = 'CTO Ad Spend, running total for the month';
const SOURCE_UNIQUE_HELP = "Distinct addresses across this source's links this month";
const SOURCE_TOTAL_SPEND_HELP = 'Unique Leads × CPL';
const FACEBOOK_ROW_HELP =
  'Every Paid Social sign-up in this range; spend is the Display Ad Spend entered by hand';
const EXTERNAL_UNIQUE_HELP = 'Distinct addresses from this external partner this month';
const EXTERNAL_COOP_REVENUE_HELP = 'Delivered-lead revenue attributed to this partner site';
const EXTERNAL_EPL_HELP = 'Co-op Revenue ÷ Unique Leads';

/**
 * The grid footer's Leads cell — the `UNIQUE_TOTAL_TITLE`-style wording this
 * task pins (the month dedupe is not the sum of the day rows).
 */
const FOOTER_LEADS_TITLE = 'Distinct addresses across the month — not the sum of daily uniques';

/**
 * Custom Page has no README-pinned help; decision 7 rules that the help says
 * the toggle does not move the click_out accept cells (`request_log` has no
 * `is_test`) — the same note the Custom Opt-in screen carries.
 */
const CUSTOM_PAGE_HELP =
  'Custom Opt-in revenue this day; the click-out accepts are not affected by the test-leads toggle';

/** Her campaign-column header title, per README §Decisions pre-resolved. */
function campaignTitle(name: string): string {
  return `${name} — unique leads delivered this day; 'capped' marks a day at its daily cap`;
}

/** A campaign was not running this day — her sheet's dash. */
const NOT_RUNNING = '–';

/** Not applicable to the Facebook row (budget, lead limit, invoices) — an em dash, unlike the grid's en-dash `NOT_RUNNING`. */
const NOT_APPLICABLE = '—';

/**
 * Task 094-04: the Source dropdown's URL key and option vocabulary. The
 * pseudo-sources (`facebook`, `unattributed`) are hardcoded — they are not
 * `site` rows; the site options come from `getSites()` merged with the ids
 * the stats response's sources block knows (a site with spend but no seeded
 * admin row must still be selectable on a deep link).
 */
const SOURCE_KEY = 'source';
const SOURCE_ALL = 'all';
const SOURCE_FACEBOOK = 'facebook';
const SOURCE_UNATTRIBUTED = 'unattributed';

/** The two split rows a conversion writes (README plan-review M3). */
const CONVERT_TO_SITE_ID = FACEBOOK_SITE_ID;

/**
 * A controlled `select` shows its value only when an `option` carries it, so
 * an effective key with no seeded site row (a deep link to a deleted site's
 * id) still renders — as the raw key rather than a wrong All-sources label.
 */
const SOURCE_BUILTIN_VALUES = new Set([SOURCE_ALL, SOURCE_FACEBOOK, SOURCE_UNATTRIBUTED]);

function bySiteLabel(a: { id: string; label: string }, b: { id: string; label: string }): number {
  const cmp = a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  return cmp !== 0 ? cmp : a.id.localeCompare(b.id);
}

/** The convert dialog's offer, shown verbatim under the 409's grain-rule message. */
const CONVERT_OFFER = 'Convert the day to per-source rows? The current number moves to Facebook.';

/**
 * QA-48: the totals row's placeholder where a value cannot compute — the
 * footer ratios divide by the month's distinct Leads, so a month with none
 * renders a faint em dash instead of the blank cell it showed before.
 */
const MISSING_TOTAL = <span className="text-faint">—</span>;

const NUM_CELL = 'text-right tabular-nums';

/**
 * The frozen grid (QA-28). The grid scrolls inside one height-bounded box, so
 * the header row sticks to its top and the Date column to its left. Every
 * sticky cell is opaque (`bg-surface`, the Card's own background) so rows
 * scrolling under it don't show through, and carries its own divider as an
 * inset shadow: the table is `border-collapse: collapse`, so the row borders
 * belong to the rows and would scroll away under a sticky cell.
 *
 * QA-46 adds the header band: a subtle tint riding on a negative-z `::after`
 * (HEAD_TINT) — inside the sticky cell's own stacking context it paints above
 * the cell's background but below its label — and a divider one step stronger
 * than the body rows' `border-line` (`--color-line-strong`, an existing
 * token).
 *
 * HF-150 makes the header page-wide: `top` reads `--page-sticky-top` (set on
 * the scroll box by the page-stickiness effect below) — 0 until the page
 * itself scrolls the box past the work area's top, then the box's rise, so
 * the header stays pinned to the top of the screen while the table is
 * anywhere on screen and rides away only with the table. Pure CSS can't say
 * this: the nearest scrollport of these cells is the box (which must stay
 * their scrollport for the inner freeze), never the page.
 */
const HEAD_TINT =
  'after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:bg-muted/10 after:content-[""]';
const PAGE_STICKY_TOP = 'top-[var(--page-sticky-top,0px)]';
const STICKY_HEAD = `sticky ${PAGE_STICKY_TOP} z-20 bg-surface shadow-[inset_0_-1px_0_var(--color-line-strong)] ${HEAD_TINT}`;
const STICKY_HEAD_DATE = `sticky ${PAGE_STICKY_TOP} left-0 z-30 bg-surface shadow-[inset_0_-1px_0_var(--color-line-strong),inset_-1px_0_0_var(--color-line)] ${HEAD_TINT}`;
const STICKY_DATE_CELL = 'sticky left-0 z-10 bg-surface shadow-[inset_-1px_0_0_var(--color-line)]';

/**
 * QA-47: alternating day rows — the hairline token as a quiet background
 * tint. The row takes it as a background; the sticky Date cell, opaque
 * against horizontally-scrolled content, carries the same tint through the
 * same `::after` overlay the header uses.
 *
 * r21 #150 (097-05): /20 sat below visual perception — the tester could not
 * tell striped and plain rows apart knowing exactly where to look — so the
 * tint rides at /40, the floor of QA's own suggestion: still the `line` token
 * at opacity only, no new color. Known interaction, deliberately left as-is:
 * the shared TableRow's `hover:bg-line/30` is now the weaker tint on odd rows
 * (a hover bump lives in ui/table.tsx, outside this task's Owns set).
 */
const STRIPE_ROW = 'bg-line/40';
const STRIPE_CELL_OVERLAY =
  'after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:bg-line/40 after:content-[""]';

/** Half-up to 2 dp — the same rule as the api's `money.ts` `round2`, so the
 * footer's recomputed ratios can never diverge from a recomputed DTO cell. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** `NN.NN%` with no space — her sheet's `0.00%`; empty when there is no value. */
function fmtPct(n: number | null): string {
  return n === null ? '' : `${n.toFixed(2)}%`;
}

/**
 * The 409 grain-rule message of a save attempt, verbatim; null when the error
 * is not a grain conflict (another failure propagates to the toast). The api
 * names the rule in every 409 it raises (`daily-spend-entry.service.ts`), so
 * matching the marker the message itself carries is exact, not a status-code
 * guess the client cannot see through the screen-local `apiFetch`.
 */
function grainConflictMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const message = err.message;
  return message.includes('a day must be either a single unsplit entry') ? message : null;
}

/**
 * The 404 missing-entry message of a null-row DELETE attempt, verbatim; null
 * when the error is anything else (another failure propagates). The api names
 * the missing day in every remove 404 it raises
 * (`daily-spend-entry.service.ts`), so the marker is exact.
 */
function missingEntryMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const message = err.message;
  return message.includes('No daily spend entry for') ? message : null;
}

interface SummaryCell {
  label: string;
  value: string;
  title?: string;
}

/** Row *Daily totals (sum of days)* — her sheet's grain, every cell additive. */
function rawSummaryCells(totals: DailySpendTotals): SummaryCell[] {
  return [
    { label: 'Leads', value: cell(totals.leadsRaw) },
    { label: 'Custom', value: fmtMoney(totals.customPageRevenue) },
    { label: 'Co-op', value: fmtMoney(totals.coopRevenue) },
    { label: 'Gross', value: fmtMoney(totals.grossRevenue) },
    { label: 'Total Revenue', value: fmtMoney(totals.netRevenue) },
    { label: 'Display', value: fmtMoney(totals.displayAdSpend) },
    { label: 'CTO', value: fmtMoney(totals.ctoAdSpend) },
    { label: 'Total Spend', value: fmtMoney(round2(totals.displayAdSpend + totals.ctoAdSpend)) },
    { label: 'ROI', value: totals.roiPct === null ? '' : `${totals.roiPct.toFixed(2)}%` },
  ];
}

/** Row *Month-deduped (billing)* — the month grain (decision 4). */
function dedupSummaryCells(totals: DailySpendTotals): SummaryCell[] {
  return [
    { label: 'Leads', value: cell(totals.leadsDeduped), title: FOOTER_LEADS_TITLE },
    { label: 'Custom', value: fmtMoney(totals.customPageRevenueDeduped) },
    { label: 'Co-op', value: fmtMoney(totals.coopRevenueDeduped) },
    {
      label: 'Gross',
      value: fmtMoney(round2(totals.customPageRevenueDeduped + totals.coopRevenueDeduped)),
    },
    { label: 'Total Revenue', value: fmtMoney(totals.netRevenueDeduped) },
    { label: 'CTO', value: fmtMoney(totals.ctoAdSpendDeduped) },
  ];
}

/** The summary card's two rows — the row name in a leading header cell. */
function SummaryRow({
  testId,
  name,
  cells,
  bordered,
}: {
  testId: string;
  name: string;
  cells: SummaryCell[];
  bordered: boolean;
}) {
  return (
    <tr data-testid={testId} className={bordered ? 'border-b border-line' : undefined}>
      <th scope="row" className="px-4 py-2 text-left align-top font-semibold whitespace-nowrap">
        {name}
      </th>
      {cells.map((c) => (
        <td key={c.label} className="px-4 py-2 align-top" title={c.title}>
          <div className="text-xs text-faint">{c.label}</div>
          <div className="text-right tabular-nums">{c.value}</div>
        </td>
      ))}
    </tr>
  );
}

export function DailySpend() {
  const range = useReportRange();
  const { from, to } = range;
  const [searchParams, setSearchParams] = useSearchParams();
  // The Traffic source filter (094-04, D3): `'all'`/absent = the pre-epic
  // view. Read from the URL so a deep link (`?source=101`) renders filtered
  // on first load; the response's `source` echo is the dropdown's controlled
  // value once data lands (deep-link correctness).
  const source = searchParams.get(SOURCE_KEY) ?? SOURCE_ALL;
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState<DailySpendStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped after a save/remove so the stats effect refetches the edited day.
  const [tick, setTick] = useState(0);
  // Best-effort site list for the dropdown (the custom-optin-stats idiom: an
  // empty list degrades the filter, not the report).
  const [sites, setSites] = useState<{ id: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    api
      .getSites()
      .then((all) => {
        if (cancelled) return;
        setSites(all.map((s) => ({ id: s.siteId, label: s.label })));
      })
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setSource = (next: string) => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next === SOURCE_ALL) updated.delete(SOURCE_KEY);
        else updated.set(SOURCE_KEY, next);
        return updated;
      },
      { replace: true }
    );
  };

  const [entryDialog, setEntryDialog] = useState<{
    date: string;
    amount: string;
    hasEntry: boolean;
    /** The 409's verbatim message, shown above the convert offer when set. */
    conflict: string | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const { success, error: toastError } = useToast();

  // Fetch on every range/toggle/source change (and after an entry edit); the
  // stale response of a superseded filter set is dropped rather than rendered.
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDailySpendStats({ from, to, includeTest, ...(source === SOURCE_ALL ? {} : { source }) })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest, source, tick]);

  const refetch = () => setTick((t) => t + 1);

  const campaigns = data?.campaigns ?? [];
  const paidSocial = data?.paidSocial ?? null;
  // The dropdown's controlled value: the URL key while loading (a filter
  // change must not snap the select back to the previous response's echo),
  // then the response's `source` echo (a deep link to an unknown site id
  // renders the api's effective key, never a mismatched select).
  const effectiveSource = loading ? source : (data?.source ?? source);
  // D3: the campaign matrix and the Traffic sources block are cross-source
  // artifacts — they render only under `all`.
  const showAllBlocks = effectiveSource === SOURCE_ALL;
  // D3: Unattributed has no display rows ever (the api's own invariant,
  // `daily-spend-stats.service.ts`) — the Display Ad Spend editor has
  // nothing to edit under that filter, so its entry point hides, the same
  // pattern as the campaign/sources blocks.
  const showEntryEditor = effectiveSource !== SOURCE_UNATTRIBUTED;
  // The site options: the admin list, plus any site id the (unfiltered)
  // response's sources block knows that `getSites()` didn't return — a
  // resolvable id stays selectable. Deduped by id, first label wins.
  const siteOptions = useMemo(() => {
    const byId = new Map<string, { id: string; label: string }>();
    for (const site of sites) byId.set(site.id, site);
    for (const row of data?.sources ?? []) {
      if (!byId.has(row.siteCode)) byId.set(row.siteCode, { id: row.siteCode, label: row.label });
    }
    return [...byId.values()].sort(bySiteLabel);
  }, [sites, data]);
  // D3: the campaign columns are a cross-source artifact — the api already
  // returns an empty `campaigns` array under a filter; gating here keeps the
  // rule true even if a response ever carried them.
  const visibleCampaigns = showAllBlocks ? campaigns : [];
  // Fixed columns (13 economics + Total Unique Leads + Monthly Total + Actions).
  const columnCount = 16 + visibleCampaigns.length;

  // Per-campaign day cells keyed by date — one lookup instead of a scan per row.
  const campaignDays = useMemo(() => {
    const byId = new Map<string, Map<string, DailySpendCampaignDay>>();
    for (const campaign of visibleCampaigns) {
      byId.set(campaign.campaignId, new Map(campaign.days.map((d) => [d.date, d])));
    }
    return byId;
  }, [visibleCampaigns]);

  // Monthly Total — her right block: CTO Ad Spend, running over the ordered days.
  const monthlyTotals = useMemo(() => {
    const running = new Map<string, number>();
    let total = 0;
    for (const day of data?.days ?? []) {
      total = round2(total + day.ctoAdSpend);
      running.set(day.date, total);
    }
    return running;
  }, [data]);

  /**
   * HF-150 — the page-stickiness effect behind `--page-sticky-top`. The app
   * scrolls in `#work` (layout.tsx), so while the grid's scroll box has risen
   * `n` px past `#work`'s visible top, the header's sticky `top` must be `n`
   * too — the header then sits at the top of the screen, over the box's own
   * scrolled content, instead of leaving with the box's top edge. The clamp:
   * never past the box's height minus one header, so once the box's bottom
   * edge reaches the pinned header it stops compensating and the header
   * rides away with the table's last rows.
   *
   * The scroll listeners are passive reads that only set the variable; the
   * box's own inner scroll needs no listener (sticky already answers to it),
   * and jsdom measures everything as 0, so tests see `0px` — the QA-28
   * in-box freeze unchanged.
   */
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = scrollBoxRef.current;
    if (box === null) return;
    // Fallback for any layout without `#work` (and for the document scroller).
    const page = box.closest<HTMLElement>('#work') ?? document.documentElement;
    const apply = () => {
      const headerHeight = box.querySelector('thead')?.getBoundingClientRect().height ?? 0;
      const rise = page.getBoundingClientRect().top - box.getBoundingClientRect().top;
      const max = Math.max(box.clientHeight - headerHeight, 0);
      box.style.setProperty('--page-sticky-top', `${Math.min(Math.max(rise, 0), max)}px`);
    };
    apply();
    page.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply);
    return () => {
      page.removeEventListener('scroll', apply);
      window.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  const openEntryDialog = (day: DailySpendDayRow) => {
    setEntryDialog({
      date: day.date,
      amount: day.displayAdSpend === null ? '' : String(day.displayAdSpend),
      hasEntry: day.displayAdSpend !== null,
      conflict: null,
    });
  };

  const handleEntrySave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (entryDialog === null || saving) return;
    const amount = Number(entryDialog.amount);
    if (entryDialog.amount.trim() === '' || Number.isNaN(amount) || amount < 0) {
      toastError('Enter an amount of 0 or more');
      return;
    }
    setSaving(true);
    try {
      if (effectiveSource === SOURCE_ALL) {
        // All view: the day's unsplit (null) row — today's behavior.
        await putDailySpendEntry(entryDialog.date, amount);
      } else if (effectiveSource === SOURCE_FACEBOOK) {
        // The null row IS Facebook's row (D3): edit it directly; a 409 means
        // the day is already split, so the split's Facebook row is the target.
        try {
          await putDailySpendEntry(entryDialog.date, amount);
        } catch (err) {
          if (grainConflictMessage(err) === null) throw err;
          await putDailySpendEntry(entryDialog.date, amount, CONVERT_TO_SITE_ID);
        }
      } else {
        try {
          await putDailySpendEntry(entryDialog.date, amount, effectiveSource);
        } catch (err) {
          const conflict = grainConflictMessage(err);
          if (conflict !== null) {
            // The day has a null row and this is a split-source write: surface
            // the grain rule verbatim and offer the explicit convert choice.
            setEntryDialog((prev) => (prev === null ? prev : { ...prev, conflict }));
            return;
          }
          throw err;
        }
      }
      success('Display Ad Spend saved');
      setEntryDialog(null);
      refetch();
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Failed to save Display Ad Spend');
    } finally {
      setSaving(false);
    }
  };

  /**
   * The convert choice (M3): DELETE the day's null row, PUT the Facebook row
   * with the moved amount, then complete the original split-source write —
   * the exact two-endpoint sequence, no dedicated convert endpoint. A
   * mid-sequence failure re-fetches and shows the day's actual state.
   *
   * The moved amount is read BEFORE the DELETE: the convert offer only shows
   * on a null-row day under a site filter, and task 094-03's contract
   * excludes the null row from that filtered day cell — so the dialog's own
   * cell is null exactly here, and the real number must come from the
   * unfiltered single-day stats.
   */
  const handleEntryConvert = async () => {
    if (entryDialog === null || saving) return;
    const amount = Number(entryDialog.amount);
    if (entryDialog.amount.trim() === '' || Number.isNaN(amount) || amount < 0) {
      toastError('Enter an amount of 0 or more');
      return;
    }
    setSaving(true);
    try {
      const unfiltered = await fetchDailySpendStats({
        from: entryDialog.date,
        to: entryDialog.date,
        includeTest,
      });
      const movedAmount = unfiltered.days.find((d) => d.date === entryDialog.date)?.displayAdSpend;
      await deleteDailySpendEntry(entryDialog.date);
      await putDailySpendEntry(entryDialog.date, movedAmount ?? 0, CONVERT_TO_SITE_ID);
      await putDailySpendEntry(entryDialog.date, amount, effectiveSource);
      success('Display Ad Spend saved');
      setEntryDialog(null);
      refetch();
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Failed to save Display Ad Spend');
      setEntryDialog(null);
      refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleEntryRemove = async () => {
    if (entryDialog === null || saving) return;
    setSaving(true);
    try {
      if (effectiveSource === SOURCE_ALL) {
        await deleteDailySpendEntry(entryDialog.date);
      } else if (effectiveSource === SOURCE_FACEBOOK) {
        // The null row first (it IS Facebook's); a 404 means the day is split
        // and the split's Facebook row is the target.
        try {
          await deleteDailySpendEntry(entryDialog.date);
        } catch (err) {
          if (missingEntryMessage(err) === null) throw err;
          await deleteDailySpendEntry(entryDialog.date, CONVERT_TO_SITE_ID);
        }
      } else {
        await deleteDailySpendEntry(entryDialog.date, effectiveSource);
      }
      success('Display Ad Spend removed');
      setEntryDialog(null);
      refetch();
    } catch (err) {
      // Keep the dialog open on error so the remove is retryable — the same
      // behavior as handleEntrySave's catch (one failure behavior for both
      // write paths; logged in the task-04 fixer report).
      toastError(err instanceof Error ? err.message : 'Failed to remove Display Ad Spend');
    } finally {
      setSaving(false);
    }
  };

  // The footer is the raw row (her sheet's arithmetic, decision 4): raw money
  // sums, Leads recomputed distinct across the month, the ratios recomputed
  // from the footer's own gross and spend over that deduped Leads.
  const totals = data?.totals;
  const footerLeads = totals?.leadsDeduped ?? 0;
  const footerSpend = totals === undefined ? 0 : round2(totals.displayAdSpend + totals.ctoAdSpend);
  const footerRatio = (numerator: number) =>
    footerLeads > 0 ? fmtMoney(round2(numerator / footerLeads)) : MISSING_TOTAL;
  const footerCoregPlusLeadPct =
    totals !== undefined && footerLeads > 0
      ? fmtPct(round2((totals.coopRevenue / 2 / footerLeads) * 100))
      : MISSING_TOTAL;
  const campaignsMonthUniques = visibleCampaigns.reduce((sum, c) => sum + c.monthUniqueLeads, 0);

  return (
    <ReportShell
      title="Daily Spend"
      desc="Her daily sheet as one screen: month totals in both dedup grains, the daily grid with per-campaign columns, the per-source buying terms, and Display Ad Spend entered by hand."
    >
      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <RangeFilters
            preset={range.preset}
            from={range.from}
            to={range.to}
            applyPreset={range.applyPreset}
            setRange={range.setRange}
            testIdPrefix="daily-spend"
          />
          <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />
          <Select
            aria-label="Source"
            data-testid="daily-source-filter"
            value={effectiveSource}
            onChange={(e) => setSource(e.target.value)}
            className="h-9 min-w-[180px]"
          >
            <option value={SOURCE_ALL}>All sources</option>
            <option value={SOURCE_FACEBOOK}>Facebook</option>
            <option value={SOURCE_UNATTRIBUTED}>Unattributed</option>
            {siteOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
            {!SOURCE_BUILTIN_VALUES.has(effectiveSource) &&
              !siteOptions.some((s) => s.id === effectiveSource) && (
                <option value={effectiveSource}>{effectiveSource}</option>
              )}
          </Select>
          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>

        {error && <ErrorBanner message={error} />}

        {totals && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                <SummaryRow
                  testId="daily-totals-raw"
                  name="Daily totals (sum of days)"
                  cells={rawSummaryCells(totals)}
                  bordered
                />
                <SummaryRow
                  testId="daily-totals-dedup"
                  name="Month-deduped (billing)"
                  cells={dedupSummaryCells(totals)}
                  bordered={false}
                />
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="mt-4 overflow-hidden">
        <div
          ref={scrollBoxRef}
          data-testid="daily-table-scroll"
          className="max-h-[75vh] overflow-auto"
        >
          <table data-testid="daily-table" className="w-full caption-bottom text-sm">
            <TableHeader>
              <TableRow>
                <TableHead className={`${STICKY_HEAD_DATE} whitespace-nowrap`}>Date</TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={CUSTOM_PAGE_HELP}
                >
                  Custom Page
                </TableHead>
                <TableHead className={`${STICKY_HEAD} text-right whitespace-nowrap`}>
                  Co-op
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={LEADS_HELP}
                >
                  Leads
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={EPL_HELP}
                >
                  EPL
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={TOTAL_REVENUE_HELP}
                >
                  Total Revenue
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={TOTAL_SPEND_HELP}
                >
                  Total Spend
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={DISPLAY_AD_SPEND_HELP}
                >
                  Display Ad Spend
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={CTO_AD_SPEND_HELP}
                >
                  CTO Ad Spend
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={CPL_HELP}
                >
                  CPL
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={COREG_PLUS_LEAD_HELP}
                >
                  Co-reg Plus Lead %
                </TableHead>
                <TableHead className={`${STICKY_HEAD} text-right whitespace-nowrap`}>
                  Co-op EPL
                </TableHead>
                <TableHead className={`${STICKY_HEAD} text-right whitespace-nowrap`}>
                  Custom Pg EPL
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={TOTAL_UNIQUE_LEADS_HELP}
                >
                  Total Unique Leads
                </TableHead>
                <TableHead
                  className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                  title={MONTHLY_TOTAL_HELP}
                >
                  Monthly Total
                </TableHead>
                {visibleCampaigns.map((campaign) => (
                  <TableHead
                    key={campaign.campaignId}
                    data-testid={`daily-camp-${campaign.campaignId}`}
                    className={`${STICKY_HEAD} text-right whitespace-nowrap`}
                    title={campaignTitle(campaign.name)}
                  >
                    {campaign.name}
                  </TableHead>
                ))}
                <TableHead className={`${STICKY_HEAD} text-right whitespace-nowrap`}>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null ? (
                <LoadingRow colSpan={columnCount} />
              ) : data.days.length === 0 ? (
                <EmptyRow colSpan={columnCount} message="No data in this range." />
              ) : (
                data.days.map((day, idx) => {
                  const striped = idx % 2 === 1;
                  return (
                    <TableRow
                      key={day.date}
                      data-testid={`daily-row-${day.date}`}
                      className={striped ? STRIPE_ROW : undefined}
                    >
                      <TableCell
                        className={`${STICKY_DATE_CELL} whitespace-nowrap${striped ? ` ${STRIPE_CELL_OVERLAY}` : ''}`}
                      >
                        {fmtDate(day.date)}
                      </TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.customPageRevenue)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.coopRevenue)}</TableCell>
                      <TableCell className={NUM_CELL}>{cell(day.leads)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.epl)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.netRevenue)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.totalSpend)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.displayAdSpend)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.ctoAdSpend)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.cpl)}</TableCell>
                      <TableCell className={NUM_CELL} data-testid={`daily-coreg-${day.date}`}>
                        {fmtPct(day.coregPlusLeadPct)}
                      </TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.coOpEpl)}</TableCell>
                      <TableCell className={NUM_CELL}>{fmtMoney(day.customPgEpl)}</TableCell>
                      <TableCell className={NUM_CELL}>{cell(day.leadsDelivered)}</TableCell>
                      <TableCell className={NUM_CELL}>
                        {fmtMoney(monthlyTotals.get(day.date) ?? 0)}
                      </TableCell>
                      {visibleCampaigns.map((campaign) => {
                        const dayCell = campaignDays.get(campaign.campaignId)?.get(day.date);
                        return (
                          <TableCell
                            key={campaign.campaignId}
                            className={NUM_CELL}
                            data-testid={`daily-camp-${campaign.campaignId}-${day.date}`}
                          >
                            {dayCell === undefined || dayCell.leads === null ? (
                              NOT_RUNNING
                            ) : (
                              <>
                                {cell(dayCell.leads)}
                                {dayCell.capped && (
                                  <sup className="ml-0.5 text-[10px] text-faint">capped</sup>
                                )}
                              </>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right">
                        {showEntryEditor && (
                          <button
                            type="button"
                            data-testid={`daily-entry-edit-${day.date}`}
                            onClick={() => openEntryDialog(day)}
                            className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
                          >
                            Edit spend
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            {data !== null && data.days.length > 0 && totals && (
              <TableFooter>
                <TableRow data-testid="daily-totals-row">
                  <TableCell className={STICKY_DATE_CELL}>Total</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(totals.customPageRevenue)}</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(totals.coopRevenue)}</TableCell>
                  <TableCell className={NUM_CELL} title={FOOTER_LEADS_TITLE}>
                    {cell(totals.leadsDeduped)}
                  </TableCell>
                  <TableCell className={NUM_CELL}>{footerRatio(totals.grossRevenue)}</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(totals.netRevenue)}</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(footerSpend)}</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(totals.displayAdSpend)}</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(totals.ctoAdSpend)}</TableCell>
                  <TableCell className={NUM_CELL}>{footerRatio(footerSpend)}</TableCell>
                  <TableCell className={NUM_CELL}>{footerCoregPlusLeadPct}</TableCell>
                  <TableCell className={NUM_CELL}>{footerRatio(totals.coopRevenue)}</TableCell>
                  <TableCell className={NUM_CELL}>
                    {footerRatio(totals.customPageRevenue)}
                  </TableCell>
                  <TableCell className={NUM_CELL}>{cell(campaignsMonthUniques)}</TableCell>
                  <TableCell className={NUM_CELL}>{fmtMoney(totals.ctoAdSpend)}</TableCell>
                  {visibleCampaigns.map((campaign) => (
                    <TableCell key={campaign.campaignId} className={NUM_CELL}>
                      {cell(campaign.monthUniqueLeads)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right" />
                </TableRow>
              </TableFooter>
            )}
          </table>
        </div>
      </Card>

      {showAllBlocks && (
        <Card className="mt-4 overflow-hidden" data-testid="daily-sources-card">
          <div className="border-b border-line px-4 py-3 text-sm font-semibold">
            Traffic sources
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="daily-sources">
              <TableHeader>
                <TableRow>
                  <TableHead>Site ID</TableHead>
                  <TableHead>Traffic source</TableHead>
                  <TableHead className="text-right" title={SOURCE_UNIQUE_HELP}>
                    Unique Leads
                  </TableHead>
                  <TableHead className="text-right">Leads (raw)</TableHead>
                  <TableHead className="text-right">CPL</TableHead>
                  <TableHead className="text-right" title={SOURCE_TOTAL_SPEND_HELP}>
                    Total Spend
                  </TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Lead Limit</TableHead>
                  <TableHead>Invoices</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data !== null && data.sources.length === 0 && paidSocial === null ? (
                  <EmptyRow colSpan={9} message="No traffic sources in this range." />
                ) : (
                  <>
                    {(data?.sources ?? []).map((source) => (
                      <TableRow key={source.id} data-testid={`daily-source-${source.id}`}>
                        <TableCell className="font-mono">{source.siteCode}</TableCell>
                        <TableCell>{source.label}</TableCell>
                        <TableCell className={NUM_CELL}>{cell(source.uniqueLeads)}</TableCell>
                        <TableCell className={NUM_CELL}>{cell(source.rawLeads)}</TableCell>
                        <TableCell className={NUM_CELL}>{fmtMoney(source.cpl)}</TableCell>
                        <TableCell className={NUM_CELL}>{fmtMoney(source.totalSpend)}</TableCell>
                        <TableCell className={NUM_CELL}>{fmtMoney(source.budget)}</TableCell>
                        <TableCell className={NUM_CELL}>
                          {source.leadLimit === null ? '' : fmtCount(source.leadLimit)}
                        </TableCell>
                        <TableCell>{source.invoiceDates.join(', ')}</TableCell>
                      </TableRow>
                    ))}
                    {paidSocial !== null && (
                      <TableRow data-testid="daily-source-facebook">
                        <TableCell>{NOT_APPLICABLE}</TableCell>
                        <TableCell title={FACEBOOK_ROW_HELP}>Facebook</TableCell>
                        <TableCell className={NUM_CELL}>{cell(paidSocial.uniqueLeads)}</TableCell>
                        <TableCell className={NUM_CELL}>{cell(paidSocial.rawLeads)}</TableCell>
                        <TableCell className={NUM_CELL}>{fmtMoney(paidSocial.cpl)}</TableCell>
                        <TableCell className={NUM_CELL}>{fmtMoney(paidSocial.spend)}</TableCell>
                        <TableCell className={NUM_CELL}>{NOT_APPLICABLE}</TableCell>
                        <TableCell className={NUM_CELL}>{NOT_APPLICABLE}</TableCell>
                        <TableCell>{NOT_APPLICABLE}</TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {data !== null && data.externalSources.length > 0 && (
        <Card className="mt-4 overflow-hidden" data-testid="daily-external-sources-card">
          <div className="border-b border-line px-4 py-3 text-sm font-semibold">
            External sources
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="daily-external-sources">
              <TableHeader>
                <TableRow>
                  <TableHead>Site</TableHead>
                  <TableHead className="text-right">Raw Leads</TableHead>
                  <TableHead className="text-right" title={EXTERNAL_UNIQUE_HELP}>
                    Unique Leads
                  </TableHead>
                  <TableHead className="text-right" title={EXTERNAL_COOP_REVENUE_HELP}>
                    Co-op Revenue
                  </TableHead>
                  <TableHead className="text-right" title={EXTERNAL_EPL_HELP}>
                    EPL
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.externalSources.map((source) => (
                  <TableRow
                    key={source.siteKey}
                    data-testid={`daily-external-source-${source.siteKey}`}
                  >
                    <TableCell className="font-mono" title={source.siteKey}>
                      {source.label ?? source.siteKey}
                    </TableCell>
                    <TableCell className={NUM_CELL}>{cell(source.rawLeads)}</TableCell>
                    <TableCell className={NUM_CELL}>{cell(source.uniqueLeads)}</TableCell>
                    <TableCell className={NUM_CELL}>{fmtMoney(source.coopRevenue)}</TableCell>
                    <TableCell className={NUM_CELL}>
                      {source.uniqueLeads > 0
                        ? fmtMoney(round2(source.coopRevenue / source.uniqueLeads))
                        : NOT_APPLICABLE}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog
        open={entryDialog !== null}
        onOpenChange={(open) => {
          if (!open) setEntryDialog(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit spend</DialogTitle>
            <DialogDescription>
              Display Ad Spend for {entryDialog?.date} (entered by hand).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEntrySave}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="displayAdSpend">Display Ad Spend ($)</Label>
                <Input
                  id="displayAdSpend"
                  type="number"
                  step="0.01"
                  min="0"
                  value={entryDialog?.amount ?? ''}
                  onChange={(e) =>
                    setEntryDialog((prev) =>
                      prev === null ? prev : { ...prev, amount: e.target.value }
                    )
                  }
                  required
                />
              </div>
              {entryDialog?.conflict !== null && entryDialog?.conflict !== undefined && (
                <div
                  data-testid="daily-entry-conflict"
                  className="space-y-2 rounded-lg border border-line bg-paper p-3 text-sm"
                >
                  <p className="text-ink">{entryDialog.conflict}</p>
                  <p className="text-muted-foreground">{CONVERT_OFFER}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              {entryDialog?.conflict !== null && entryDialog?.conflict !== undefined && (
                <Button
                  type="button"
                  data-testid="daily-entry-convert"
                  onClick={handleEntryConvert}
                  disabled={saving}
                >
                  Convert
                </Button>
              )}
              {entryDialog?.hasEntry && (
                <Button
                  type="button"
                  variant="destructive"
                  data-testid="daily-entry-remove"
                  onClick={handleEntryRemove}
                  disabled={saving}
                >
                  Remove
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setEntryDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ReportShell>
  );
}
